// Stedi Mock Service
//
// Returns synthetic 271 responses so the whole eligibility feature can be built
// and demoed before Stedi credentials exist — and, afterwards, so we can test
// the ugly cases the sandbox does not reproduce (carve-outs above all).
//
// Scenario selection is driven by the member ID prefix, so a tester can trigger
// any case by typing a member ID rather than flipping a config flag:
//
//   CARVE1234   → BCBS primary with behavioral benefits carved out to Carelon
//   CARVE9999   → carve-out that returns a DIFFERENT member ID for the MBHO
//   INACT1234   → terminated coverage
//   DEDUCT1234  → high-deductible plan, deductible not met
//   AUTH1234    → active but prior auth required, with visit limits
//   NOTEL1234   → office visits only, telehealth explicitly not covered
//   ERR1234     → payer rejects the request (member not found)
//   SPARSE1234  → payer answers "active" and nothing else
//   (anything)  → active coverage, $30 copay, telehealth covered
//
// The shapes here mirror Stedi's documented eligibility response. They are our
// best model of it, not a capture from the wire — re-check them against a real
// sandbox call in Phase 2 and correct any drift here first, since these doubles
// are what the parser is tested against.

import {
  EligibilityRequest,
  EligibilityResponse,
} from '../types/eligibility';

export type MockScenarioKey =
  | 'active-standard'
  | 'carve-out-carelon'
  | 'carve-out-new-member-id'
  | 'inactive'
  | 'deductible-not-met'
  | 'prior-auth-required'
  | 'no-telehealth'
  | 'payer-error'
  | 'sparse-response';

export interface MockScenario {
  key: MockScenarioKey;
  label: string;
  /** What this scenario proves the UI handles. */
  description: string;
  memberIdPrefix: string;
}

export const MOCK_SCENARIOS: MockScenario[] = [
  {
    key: 'active-standard',
    label: 'Active — standard',
    description: 'Active coverage, $30 copay, deductible met, telehealth covered.',
    memberIdPrefix: '(default)',
  },
  {
    key: 'carve-out-carelon',
    label: 'Behavioral carve-out (Carelon)',
    description:
      'Medical plan active, behavioral benefits administered by Carelon. Requires a second call.',
    memberIdPrefix: 'CARVE',
  },
  {
    key: 'carve-out-new-member-id',
    label: 'Carve-out with new member ID',
    description:
      'Magellan carve-out that returns a different member ID — second call must use it, not the original.',
    memberIdPrefix: 'CARVE9',
  },
  {
    key: 'inactive',
    label: 'Inactive / terminated',
    description: 'Coverage terminated. Appointment should be flagged before the visit.',
    memberIdPrefix: 'INACT',
  },
  {
    key: 'deductible-not-met',
    label: 'Deductible not met',
    description: 'High-deductible plan, $2,450 remaining, 20% coinsurance after.',
    memberIdPrefix: 'DEDUCT',
  },
  {
    key: 'prior-auth-required',
    label: 'Prior auth + visit limit',
    description: 'Active, but prior authorization required and 12 of 20 visits remaining.',
    memberIdPrefix: 'AUTH',
  },
  {
    key: 'no-telehealth',
    label: 'Telehealth not covered',
    description: 'Office visits only — the case that matters most for a telehealth-first practice.',
    memberIdPrefix: 'NOTEL',
  },
  {
    key: 'payer-error',
    label: 'Payer error',
    description: 'Member not found. Verifies error rendering and that we do not cache failures.',
    memberIdPrefix: 'ERR',
  },
  {
    key: 'sparse-response',
    label: 'Sparse response',
    description:
      'Payer says "active" and nothing else. Every benefit field must read "Unknown", never "$0".',
    memberIdPrefix: 'SPARSE',
  },
];

/** Trading partner IDs the mock treats as behavioral carve-out administrators. */
export const MOCK_MBHO_PAYER_IDS = ['CARBH', '01260'];

const MOCK_LATENCY_MS = 700;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function todayYyyyMmDd(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}${month}${day}`;
}

// ─── Response builders ────────────────────────────────────────────────

function baseResponse(
  request: EligibilityRequest,
  payerName: string,
  payerId: string
): EligibilityResponse {
  return {
    meta: {
      senderId: 'STEDI',
      submitterId: 'MYCLINICCORE',
      traceId: `mock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      applicationMode: 'mock',
    },
    controlNumber: request.controlNumber,
    tradingPartnerServiceId: request.tradingPartnerServiceId,
    provider: {
      providerOrgName: request.provider.organizationName,
      npi: request.provider.npi,
    },
    subscriber: {
      memberId: request.subscriber.memberId,
      firstName: request.subscriber.firstName,
      lastName: request.subscriber.lastName,
      dateOfBirth: request.subscriber.dateOfBirth,
      gender: 'F',
    },
    payer: {
      entityIdentifier: 'Payer',
      entityType: 'Non-Person Entity',
      name: payerName,
      payorIdentification: payerId,
    },
    planInformation: {
      groupNumber: 'GRP0092841',
      groupDescription: 'CHOICE PLUS PPO',
      planDescription: 'CHOICE PLUS PPO',
    },
    planDateInformation: {
      planBegin: '20260101',
      eligibilityBegin: '20260101',
    },
    planStatus: [
      {
        statusCode: '1',
        status: 'Active Coverage',
        planDetails: 'CHOICE PLUS PPO',
        serviceTypeCodes: request.encounter.serviceTypeCodes,
      },
    ],
    benefitsInformation: [],
  };
}

function activeStandard(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'AETNA', '60054');
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      coverageLevel: 'Individual',
      serviceTypeCodes: ['30', 'MH', 'A6'],
      serviceTypes: ['Health Benefit Plan Coverage', 'Mental Health', 'Psychotherapy'],
      insuranceTypeCode: 'PR',
      insuranceType: 'Preferred Provider Organization (PPO)',
      planCoverage: 'CHOICE PLUS PPO',
      inPlanNetworkIndicatorCode: 'Y',
      inPlanNetworkIndicator: 'Yes',
    },
    {
      code: 'B',
      name: 'Co-Payment',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      serviceTypes: ['Mental Health', 'Psychotherapy'],
      timeQualifierCode: '27',
      timeQualifier: 'Visit',
      benefitAmount: '30',
      inPlanNetworkIndicatorCode: 'Y',
      authOrCertIndicator: 'N',
      eligibilityAdditionalInformationList: [
        { codeListQualifierCode: 'ZZ', industryCode: '02', codeCategory: 'Place of Service' },
      ],
      additionalInformation: [{ description: 'TELEHEALTH VISITS COVERED AT SAME COST SHARE' }],
    },
    {
      code: 'C',
      name: 'Deductible',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '23',
      timeQualifier: 'Calendar Year',
      benefitAmount: '1500',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'C',
      name: 'Deductible',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      benefitAmount: '0',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'G',
      name: 'Out of Pocket (Stop Loss)',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '23',
      benefitAmount: '6000',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'G',
      name: 'Out of Pocket (Stop Loss)',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      benefitAmount: '4120',
      inPlanNetworkIndicatorCode: 'Y',
    },
  ];
  return response;
}

/**
 * The case this whole feature exists for: the medical plan is active and looks
 * fine, but behavioral benefits are administered by someone else entirely.
 * Billing the primary payer here is how claims get denied.
 */
function carveOutCarelon(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'BLUE CROSS BLUE SHIELD OF ILLINOIS', '00621');
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      serviceTypes: ['Health Benefit Plan Coverage'],
      insuranceTypeCode: 'PR',
      planCoverage: 'BLUE CHOICE PPO',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A4', 'A6'],
      serviceTypes: ['Mental Health', 'Psychiatric', 'Psychotherapy'],
      planCoverage: 'BEHAVIORAL HEALTH ADMINISTERED BY CARELON',
      benefitsRelatedEntities: [
        {
          entityIdentifier: 'Third-Party Administrator',
          entityType: 'Non-Person Entity',
          entityName: 'CARELON BEHAVIORAL HEALTH',
          entityIdentification: 'PI',
          entityIdentificationValue: 'CARBH',
          address: {
            address1: '1831 Chestnut Street',
            city: 'St. Louis',
            state: 'MO',
            postalCode: '63103',
          },
          contactInformation: {
            name: 'CARELON BEHAVIORAL HEALTH',
            contacts: [
              { communicationMode: 'Telephone', communicationNumber: '8005551234' },
              { communicationMode: 'Uniform Resource Locator (URL)', communicationNumber: 'carelonbehavioralhealth.com' },
            ],
          },
        },
      ],
      additionalInformation: [
        { description: 'MENTAL HEALTH BENEFITS MANAGED BY CARELON — CONTACT FOR BENEFIT DETAIL' },
      ],
    },
    {
      code: 'C',
      name: 'Deductible',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '23',
      benefitAmount: '2000',
      inPlanNetworkIndicatorCode: 'Y',
    },
  ];
  return response;
}

/** Same carve-out shape, but the MBHO uses its own member ID. */
function carveOutNewMemberId(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'UNITED HEALTHCARE', '87726');
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      planCoverage: 'CHOICE PLUS',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      planCoverage: 'BEHAVIORAL HEALTH CARVE-OUT',
      benefitsRelatedEntities: [
        {
          entityIdentifier: 'Third-Party Administrator',
          entityType: 'Non-Person Entity',
          entityName: 'MAGELLAN BEHAVIORAL HEALTH',
          entityIdentification: 'MI',
          entityIdentificationValue: 'MGN88217743',
          contactInformation: {
            name: 'MAGELLAN BEHAVIORAL HEALTH',
            contacts: [{ communicationMode: 'Telephone', communicationNumber: '8005559876' }],
          },
        },
      ],
    },
  ];
  return response;
}

/** What the MBHO returns on the second pass. */
function mbhoSecondPass(request: EligibilityRequest): EligibilityResponse {
  const isMagellan = request.tradingPartnerServiceId === '01260';
  const response = baseResponse(
    request,
    isMagellan ? 'MAGELLAN BEHAVIORAL HEALTH' : 'CARELON BEHAVIORAL HEALTH',
    isMagellan ? '01260' : 'CARBH'
  );
  response.planInformation = {
    groupNumber: 'BH0092841',
    planDescription: 'BEHAVIORAL HEALTH OUTPATIENT',
  };
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6', 'CF'],
      serviceTypes: ['Mental Health', 'Psychotherapy', 'Mental Health Provider — Outpatient'],
      planCoverage: 'BEHAVIORAL HEALTH OUTPATIENT',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'B',
      name: 'Co-Payment',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      timeQualifierCode: '27',
      timeQualifier: 'Visit',
      benefitAmount: '25',
      inPlanNetworkIndicatorCode: 'Y',
      authOrCertIndicator: 'N',
      eligibilityAdditionalInformationList: [
        { codeListQualifierCode: 'ZZ', industryCode: '10', codeCategory: 'Place of Service' },
      ],
      additionalInformation: [{ description: 'TELEHEALTH IN PATIENT HOME COVERED' }],
    },
    {
      code: 'F',
      name: 'Limitations',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['A6'],
      timeQualifierCode: '23',
      timeQualifier: 'Calendar Year',
      quantityQualifierCode: 'VS',
      quantityQualifier: 'Visits',
      benefitQuantity: '30',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'F',
      name: 'Limitations',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['A6'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      quantityQualifierCode: 'VS',
      benefitQuantity: '22',
      inPlanNetworkIndicatorCode: 'Y',
    },
  ];
  return response;
}

function inactive(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'CIGNA', '62308');
  response.planDateInformation = {
    planBegin: '20250101',
    planEnd: '20260630',
    eligibilityEnd: '20260630',
  };
  response.planStatus = [
    {
      statusCode: '6',
      status: 'Inactive',
      planDetails: 'COVERAGE TERMINATED',
      serviceTypeCodes: request.encounter.serviceTypeCodes,
    },
  ];
  response.benefitsInformation = [
    {
      code: '6',
      name: 'Inactive',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30', 'MH'],
      planCoverage: 'COVERAGE TERMINATED 06/30/2026',
      benefitsDateInformation: { planEnd: '20260630' },
      additionalInformation: [{ description: 'POLICY TERMINATED — MEMBER NO LONGER ELIGIBLE' }],
    },
  ];
  return response;
}

function deductibleNotMet(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'BLUE CROSS BLUE SHIELD OF TEXAS', '84980');
  response.planInformation = {
    groupNumber: 'HDHP2026',
    planDescription: 'BLUE ADVANTAGE HDHP',
  };
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30', 'MH', 'A6'],
      planCoverage: 'BLUE ADVANTAGE HDHP',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'C',
      name: 'Deductible',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '23',
      timeQualifier: 'Calendar Year',
      benefitAmount: '3000',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'C',
      name: 'Deductible',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      benefitAmount: '2450',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'A',
      name: 'Co-Insurance',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      // Fractional convention — the parser must render this as 20%, not 0.2%.
      benefitPercent: '0.2',
      inPlanNetworkIndicatorCode: 'Y',
      eligibilityAdditionalInformationList: [
        { codeListQualifierCode: 'ZZ', industryCode: '02', codeCategory: 'Place of Service' },
      ],
    },
    {
      code: 'G',
      name: 'Out of Pocket (Stop Loss)',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      benefitAmount: '5200',
      inPlanNetworkIndicatorCode: 'Y',
    },
  ];
  return response;
}

function priorAuthRequired(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'OPTUM BEHAVIORAL HEALTH', '87726');
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A4', 'A6'],
      planCoverage: 'BEHAVIORAL HEALTH OUTPATIENT',
      inPlanNetworkIndicatorCode: 'Y',
      authOrCertIndicator: 'Y',
      additionalInformation: [
        { description: 'PRIOR AUTHORIZATION REQUIRED AFTER INITIAL EVALUATION' },
      ],
    },
    {
      code: 'B',
      name: 'Co-Payment',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['A6'],
      timeQualifierCode: '27',
      benefitAmount: '40',
      inPlanNetworkIndicatorCode: 'Y',
      authOrCertIndicator: 'Y',
      eligibilityAdditionalInformationList: [
        { codeListQualifierCode: 'ZZ', industryCode: '02', codeCategory: 'Place of Service' },
      ],
    },
    {
      code: 'F',
      name: 'Limitations',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['A6'],
      timeQualifierCode: '23',
      timeQualifier: 'Calendar Year',
      quantityQualifierCode: 'VS',
      quantityQualifier: 'Visits',
      benefitQuantity: '20',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'F',
      name: 'Limitations',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['A6'],
      timeQualifierCode: '29',
      timeQualifier: 'Remaining',
      quantityQualifierCode: 'VS',
      benefitQuantity: '12',
      inPlanNetworkIndicatorCode: 'Y',
    },
  ];
  return response;
}

function noTelehealth(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'HORIZON BLUE CROSS BLUE SHIELD OF NEW JERSEY', '22099');
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30', 'MH', 'A6'],
      planCoverage: 'HORIZON DIRECT ACCESS',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'B',
      name: 'Co-Payment',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      timeQualifierCode: '27',
      benefitAmount: '35',
      inPlanNetworkIndicatorCode: 'Y',
    },
    {
      code: 'I',
      name: 'Non-Covered',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['MH', 'A6'],
      planCoverage: 'OFFICE VISITS ONLY',
      eligibilityAdditionalInformationList: [
        { codeListQualifierCode: 'ZZ', industryCode: '02', codeCategory: 'Place of Service' },
      ],
      additionalInformation: [{ description: 'TELEHEALTH SERVICES NOT COVERED UNDER THIS PLAN' }],
    },
  ];
  return response;
}

function payerError(request: EligibilityRequest): EligibilityResponse {
  return {
    meta: {
      senderId: 'STEDI',
      traceId: `mock-err-${Date.now()}`,
      applicationMode: 'mock',
    },
    controlNumber: request.controlNumber,
    tradingPartnerServiceId: request.tradingPartnerServiceId,
    errors: [
      {
        code: '75',
        description: 'Subscriber/Insured Not Found',
        field: 'subscriber.memberId',
        followupAction: 'Please correct and resubmit',
        location: '2100B',
        possibleResolutions:
          'Verify the member ID, date of birth, and name exactly as they appear on the insurance card.',
      },
    ],
  };
}

/** Payer answers the question and volunteers nothing else. Common with smaller plans. */
function sparseResponse(request: EligibilityRequest): EligibilityResponse {
  const response = baseResponse(request, 'AMBETTER', '68069');
  response.planInformation = {};
  response.planDateInformation = {};
  response.benefitsInformation = [
    {
      code: '1',
      name: 'Active Coverage',
      coverageLevelCode: 'IND',
      serviceTypeCodes: ['30'],
    },
  ];
  return response;
}

// ─── Dispatch ─────────────────────────────────────────────────────────

const SCENARIO_BUILDERS: Record<MockScenarioKey, (r: EligibilityRequest) => EligibilityResponse> = {
  'active-standard': activeStandard,
  'carve-out-carelon': carveOutCarelon,
  'carve-out-new-member-id': carveOutNewMemberId,
  inactive,
  'deductible-not-met': deductibleNotMet,
  'prior-auth-required': priorAuthRequired,
  'no-telehealth': noTelehealth,
  'payer-error': payerError,
  'sparse-response': sparseResponse,
};

export class StediMockService {
  /**
   * Pick a scenario from the member ID.
   * CARVE9 is checked before CARVE so the more specific prefix wins.
   */
  static resolveScenario(memberId?: string): MockScenarioKey {
    const id = (memberId || '').trim().toUpperCase();
    if (id.startsWith('CARVE9')) return 'carve-out-new-member-id';
    if (id.startsWith('CARVE')) return 'carve-out-carelon';
    if (id.startsWith('INACT')) return 'inactive';
    if (id.startsWith('DEDUCT')) return 'deductible-not-met';
    if (id.startsWith('AUTH')) return 'prior-auth-required';
    if (id.startsWith('NOTEL')) return 'no-telehealth';
    if (id.startsWith('ERR')) return 'payer-error';
    if (id.startsWith('SPARSE')) return 'sparse-response';
    return 'active-standard';
  }

  static listScenarios(): MockScenario[] {
    return MOCK_SCENARIOS;
  }

  /** True when this request is the MBHO second pass rather than a primary check. */
  static isCarveOutRequest(request: EligibilityRequest): boolean {
    return MOCK_MBHO_PAYER_IDS.includes(request.tradingPartnerServiceId);
  }

  /**
   * Stand-in for a real Stedi eligibility call.
   * Latency is simulated so loading and cancellation states get exercised.
   */
  static async checkEligibility(request: EligibilityRequest): Promise<EligibilityResponse> {
    await delay(MOCK_LATENCY_MS);

    if (this.isCarveOutRequest(request)) {
      return mbhoSecondPass(request);
    }

    const scenario = this.resolveScenario(request.subscriber.memberId);
    const response = SCENARIO_BUILDERS[scenario](request);

    if (!response.planDateInformation?.planEnd && response.planDateInformation) {
      response.planDateInformation.lastUpdated = todayYyyyMmDd();
    }

    return response;
  }
}
